/**
 * A chave de um conector, colada na tela.
 * Vai direto para o servidor, que a guarda cifrada; a tela nunca a recebe de
 * volta, so os 4 ultimos caracteres. Nao ha "ver a chave": para trocar, cola
 * outra. O campo e de senha e esvazia depois de salvar; o valor nao vai para
 * estado global, cache, localStorage nem console.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSessao } from '../../sessao.tsx';
import { gestao } from './api.ts';
import { useTextos } from './i18n.ts';

type Rota = 'conector_chave_salvar';

export function usePodeChaves(): boolean {
  const { estado } = useSessao();
  return estado.fase === 'dentro' && estado.eu.permissoes.includes('integracoes.chaves');
}

export function SituacaoChave({ salva, final }: { salva: boolean; final: string | null }) {
  const t = useTextos();
  return <span className="integracoes__nota">
    {salva ? `${t.chaveSalva} ${final ?? ''}` : t.semChaveColada}
  </span>;
}

export function CampoChave({ rota, id, salva, final }: {
  rota: Rota; id: string; salva: boolean; final: string | null;
}) {
  const t = useTextos();
  const qc = useQueryClient();
  const pode = usePodeChaves();
  const [ocupado, setOcupado] = useState(false);
  const [mensagem, setMensagem] = useState<{ ok: boolean; texto: string } | null>(null);
  if (!pode) return <SituacaoChave salva={salva} final={final} />;

  const erroDe = (e: unknown) => {
    const codigo = e instanceof Error ? e.message : '';
    return codigo === 'chave_mestra_ausente' ? t.erroChaveMestra
      : codigo === 'chave_invalida' ? t.erroChaveInvalida
        : codigo === 'chave_e_referencia' ? t.erroChaveReferencia : t.erro;
  };
  const enviar = async (chave: string | null, form?: HTMLFormElement) => {
    setOcupado(true);
    setMensagem(null);
    try {
      await gestao(rota, { id, chave });
      form?.reset();
      setMensagem({ ok: true, texto: chave === null ? t.sucesso : t.chaveSalvaOk });
      await qc.invalidateQueries({ queryKey: ['integracoes'] });
    } catch (e) {
      setMensagem({ ok: false, texto: erroDe(e) });
    } finally {
      setOcupado(false);
    }
  };

  return <form className="integracoes__chave" onSubmit={(e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const chave = String(new FormData(form).get('chave') ?? '');
    void enviar(chave, form);
  }}>
    <SituacaoChave salva={salva} final={final} />
    <label>{t.chaveColada}
      <input name="chave" type="password" required autoComplete="off" spellCheck={false}
        maxLength={4096} placeholder={t.colarChave} />
    </label>
    <button disabled={ocupado}>{t.salvarChave}</button>
    {salva && <button type="button" disabled={ocupado} onClick={() => {
      if (window.confirm(t.confirmarTirar)) void enviar(null);
    }}>{t.tirarChave}</button>}
    {mensagem !== null && <p role={mensagem.ok ? 'status' : 'alert'}
      className={mensagem.ok ? 'integracoes__nota' : 'integracoes__nota integracoes__nota--aviso'}>
      {mensagem.texto}</p>}
  </form>;
}
